import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { SitesContext } from "../../contexts/sitesContext";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import TextField from "@mui/material/TextField";

import styles from "./login.module.css";

const Login = () => {
  const navigate = useNavigate();
  const {
    setJwtToken,
    setUserId,
    setIsSubscribed,
    setForeverFree,
    setWithGoogle,
    setEmail,
    themeObj,
    theme,
  } = useContext(SitesContext);

  const filledInputProps = { disableUnderline: true };

  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordShowing, setIsPasswordShowing] = useState(false);

  useEffect(() => {
    document.body.style.backgroundImage = themeObj.landingBackground;
  }, [theme]);

  const onCancel = () => {
    setUsernameOrEmail("");
    setPassword("");

    navigate("/");
  };

  const onSubmit = (e) => {
    e.preventDefault();

    axios
      .post(
        `${process.env.REACT_APP_API_BASE}/api/users/login`,
        {
          usernameOrEmail,
          password,
        },
        { withCredentials: true }
      )
      .then((res) => {
        setEmail(res.data.email);
        setJwtToken(res.data.token);
        setUserId(res.data.id);
        setIsSubscribed(res.data.isSubscribed);
        setForeverFree(Boolean(res.data.foreverFree));
        setWithGoogle(res.data.google);
        navigate("/dashboard");
        localStorage.setItem("mostlinkUserId", res.data.id);
        toast("Successfully logged in.", { type: "success", theme });
      })
      .catch((err) => {
        if (err.response.data.error === "Please verify your email.") {
          navigate("/account/resend-verification");
        }
        toast(err.response.data.error, { type: "error", theme });
      });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      onSubmit(e);
    }
  };

  // Send only Google's signed ID token; the API resolves the account from it.
  const responseGoogle = ({ credential }) => {
    if (!credential) {
      toast("Google sign-in did not complete. Please try again.", {
        type: "error",
        theme,
      });
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_BASE}/api/users/login/google`,
        { credential },
        { withCredentials: true }
      )
      .then((res) => {
        setEmail(res.data.email);
        setJwtToken(res.data.token);
        setUserId(res.data.id);
        setIsSubscribed(res.data.isSubscribed);
        setForeverFree(Boolean(res.data.foreverFree));
        setWithGoogle(res.data.google);
        navigate("/dashboard");
        localStorage.setItem("mostlinkUserId", res.data.id);
        toast("Successfully logged in.", { type: "success", theme });
      })
      .catch((err) => {
        toast(
          err.response?.data?.error || "Could not log in. Please try again.",
          { type: "error", theme }
        );
      });
  };

  const failureGoogle = () =>
    toast("Google sign-in failed. Please try again.", {
      type: "error",
      theme,
    });

  return (
    <div className={styles.loginContainer}>
      <div className={styles.login}>
        <h1>Log in</h1>
        <GoogleLogin
          onSuccess={responseGoogle}
          onError={failureGoogle}
          text="continue_with"
          theme={theme === "dark" ? "filled_black" : "outline"}
        />
        <h3 style={{ color: themeObj.accentColor }}>
          <Link to="/account/register" style={{ color: themeObj.accentColor }}>
            Don't have an account? Register instead.
          </Link>
        </h3>
        <form
          onSubmit={onSubmit}
          onKeyDown={onKeyDown}
          className={styles.loginForm}
        >
          <label htmlFor="username">Username or email</label>
          <TextField
            type="text"
            value={usernameOrEmail}
            onChange={(e) => setUsernameOrEmail(e.target.value)}
            id="username"
            variant="filled"
            className={styles.textField}
            size="small"
            InputProps={filledInputProps}
          />

          <label htmlFor="password">Password</label>
          <div className={styles.passwordAndEyeIcon}>
            <TextField
              type={isPasswordShowing ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              id="password"
              className={`${styles.password} ${styles.textField}`}
              variant="filled"
              size="small"
              InputProps={filledInputProps}
            />
            <FontAwesomeIcon
              color={theme === "dark" ? "#ffffff" : "#000000"}
              icon={isPasswordShowing ? ["fas", "eye"] : ["fas", "eye-slash"]}
              onClick={() => setIsPasswordShowing(!isPasswordShowing)}
              className={styles.eyeIcon}
            />
          </div>

          <h3 style={{ color: themeObj.accentColor }}>
            <Link
              to="/account/forgot-password"
              style={{ color: themeObj.accentColor }}
            >
              Forgot password
            </Link>
          </h3>

          <div className={styles.loginFormButtons}>
            <button className={styles.cancelButton} onClick={onCancel}>
              Cancel
            </button>
            <button className={styles.submitButton} type="submit">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
